import React, { Component } from 'react';
import { Redirect, NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { USER_ROLE_ADMIN } from 'common/userRoles';
import H1 from 'client/components/H1';
import SrLogo from 'client/components/SrLogo';
import SrDataTableHouseholdDashboard from 'client/components/SrDataTableHouseholdDashboard';
import SrSearchByPhoneHouseholdIdAndRedirectForm from 'client/components/SrSearchByPhoneAndRedirectForm';

import {
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_REQUESTED,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_STARTED,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_COMPLETED,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_PAID,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_DELIVERED,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_ACCEPTED,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_LEASE_SIGNED,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_ALL,
} from 'common/dataTableConstants';

import {
  SCREEN_ID_HOUSEHOLD_DASHBOARD_APPLICATION_INITIAL,
  SCREEN_ID_HOUSEHOLD_DASHBOARD_APPLICATION_STARTED,
  SCREEN_ID_HOUSEHOLD_DASHBOARD_APPLICATION_COMPLETED,
  SCREEN_ID_HOUSEHOLD_DASHBOARD_APPLICATION_PAID,
  SCREEN_ID_HOUSEHOLD_DASHBOARD_APPLICATION_DELIVERED,
  SCREEN_ID_HOUSEHOLD_DASHBOARD_APPLICATION_ACCEPTED,
  SCREEN_ID_HOUSEHOLD_DASHBOARD_LEASE_SIGNED,
  SCREEN_ID_HOUSEHOLD_DASHBOARD_ALL,
} from 'client/constants/screenIds';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles from 'client/helpers/allowedRoles';
import branch from 'client/core/branch';
import HouseholdActions from 'client/actions/Household';

const HOUSEHOLD_DASHBOARD_PAGE_PATH_TO_PAGE_PARAMS_MAPPING = {
  'wants-to-apply': {
    headline: 'wants to apply',
    screenId: SCREEN_ID_HOUSEHOLD_DASHBOARD_APPLICATION_INITIAL,
    dataTableViewId: DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_REQUESTED,
  },
  started: {
    headline: 'started',
    screenId: SCREEN_ID_HOUSEHOLD_DASHBOARD_APPLICATION_STARTED,
    dataTableViewId: DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_STARTED,
  },
  completed: {
    headline: 'completed',
    screenId: SCREEN_ID_HOUSEHOLD_DASHBOARD_APPLICATION_COMPLETED,
    dataTableViewId: DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_COMPLETED,
  },
  paid: {
    headline: 'paid',
    screenId: SCREEN_ID_HOUSEHOLD_DASHBOARD_APPLICATION_PAID,
    dataTableViewId: DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_PAID,
  },
  delivered: {
    headline: 'delivered',
    screenId: SCREEN_ID_HOUSEHOLD_DASHBOARD_APPLICATION_DELIVERED,
    dataTableViewId: DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_DELIVERED,
  },
  accepted: {
    headline: 'accepted',
    screenId: SCREEN_ID_HOUSEHOLD_DASHBOARD_APPLICATION_ACCEPTED,
    dataTableViewId: DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_APPLICATION_ACCEPTED,
  },
  'lease-signed': {
    headline: 'lease signed',
    screenId: SCREEN_ID_HOUSEHOLD_DASHBOARD_LEASE_SIGNED,
    dataTableViewId: DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_LEASE_SIGNED,
  },
  all: {
    headline: 'all households',
    screenId: SCREEN_ID_HOUSEHOLD_DASHBOARD_ALL,
    dataTableViewId: DATA_TABLE_VIEW_ID_HOUSEHOLD_DASHBOARD_ALL,
  },
};

const householdDashboardLinks = [
  {
    label: 'wta',
    value: '/admin/household/wants-to-apply',
  },
  {
    label: 'started',
    value: '/admin/household/started',
  },
  {
    label: 'completed',
    value: '/admin/household/completed',
  },
  {
    label: 'paid',
    value: '/admin/household/paid',
  },
  {
    label: 'delivered',
    value: '/admin/household/delivered',
  },
  {
    label: 'accepted',
    value: '/admin/household/accepted',
  },
  {
    label: 'signed',
    value: '/admin/household/lease-signed',
  },
];

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(({ match: { params: { householdStatePath } } }) =>
  householdStatePath
    ? HOUSEHOLD_DASHBOARD_PAGE_PATH_TO_PAGE_PARAMS_MAPPING[householdStatePath].screenId
    : SCREEN_ID_HOUSEHOLD_DASHBOARD_APPLICATION_INITIAL,
)
@withScrollToTop
@branch({
  householdAdminsList: ['householdAdminsList'],
})
class HouseholdDashboardPage extends Component {
  static fetchData(match, inject) {
    return HouseholdActions.getHouseholdAdminsList(inject);
  }

  static propTypes = {
    householdAdminsList: PropTypes.array.isRequired,
  };

  static getPageMetadata = () => ({
    pageTitle: 'Admin Household Dashboard | Sunroom Rentals',
    section: 'Admin',
  });

  render() {
    const {
      match: {
        params: { householdStatePath, userId },
      },
      householdAdminsList,
    } = this.props;
    const pageParams = HOUSEHOLD_DASHBOARD_PAGE_PATH_TO_PAGE_PARAMS_MAPPING[householdStatePath];

    if (!pageParams) {
      return <Redirect to={householdDashboardLinks[0].value} />;
    }

    const { headline, dataTableViewId } = pageParams;

    return (
      <div className="page-householdDashboard">
        <div className="admin-navbar">
          <div className="logo-container">
            <SrLogo redirectTo="/admin" />
          </div>
          <div className="admin-navbar-links-container">
            {householdDashboardLinks.map(({ label, value }) => (
              <NavLink key={`${label}-${value}`} to={value}>
                {label}
              </NavLink>
            ))}
          </div>
        </div>
        <SrSearchByPhoneHouseholdIdAndRedirectForm
          getRedirectionPath={(user) => `/admin/household/all/${user.id}`}
        />
        <div className="admin-subnav">
          <div className="admin-subnav-left">
            <H1>{`${headline}${userId ? ' by user #' + userId : ''}`}</H1>
          </div>
        </div>
        <SrDataTableHouseholdDashboard
          viewId={dataTableViewId}
          userId={parseInt(userId, 10)}
          householdAdminsList={householdAdminsList}
        />
      </div>
    );
  }
}

HouseholdDashboardPage.displayName = 'HouseholdDashboardPage';

export default HouseholdDashboardPage;
